import { AfterViewInit, Directive, OnDestroy } from '@angular/core';
import { ICallableFunction } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { AbstractAdyenPaymentComponent } from './abstract-adyen-payment.component';
import { Adyen } from './adyen.namespace';

@Directive()
export abstract class AbstractAdyen3dsComponent<T extends Adyen.IPaymentMethod, D extends Adyen.IComponentOptions>
    extends AbstractAdyenPaymentComponent<T, D>
    implements AfterViewInit, OnDestroy
{
    /**
     * threeDS2DeviceFingerprint component
     * @type {Adyen.IComponent | undefined}
     */
    protected threeDS2IdentifyComponent?: Adyen.IComponent;

    /**
     * threeDS2Challenge component
     * @type {Adyen.IComponent | undefined}
     */
    protected threeDS2ChallengeComponent?: Adyen.IComponent;

    ngAfterViewInit() {
        super.ngAfterViewInit();
        if (this.containerElement) {
            this.subscriptions.add(
                this.app.events.on(Checkout.EVENTS.SubmitError).subscribe((errorData: Checkout.ISubmitErrorEventData) => {
                    if (this.isSelectedInRequest(errorData.request) && this.adyenCheckout && !this.component) {
                        this.onError(undefined, true);
                    }
                })
            );

            this.subscriptions.add(
                this.app.events.on(Checkout.EVENTS.SubmitSuccess).subscribe((successData: Checkout.ISubmitSuccessEventData) => {
                    if (this.isSelectedInRequest(successData.request) && this.adyenCheckout) {
                        this.onCheckoutSuccess(successData);
                    }
                })
            );

            this.subscriptions.add(this.app.events.on(Checkout.EVENTS.SuccessLoadError).subscribe(() => this.initComponent()));
        }
    }

    ////////////////////////////////////////////
    ////////
    ////////    Cards component methods
    ////////
    ////////////////////////////////////////////

    /**
     * Removes components
     */
    protected unmount(): void {
        if (this.threeDS2ChallengeComponent) {
            this.threeDS2ChallengeComponent.unmount();
            this.threeDS2ChallengeComponent = undefined;
        }
        if (this.threeDS2IdentifyComponent) {
            this.threeDS2IdentifyComponent.unmount();
            this.threeDS2IdentifyComponent = undefined;
        }
        super.unmount();
    }

    ////////////////////////////////////////////
    ////////
    ////////    Checkout callback
    ////////
    ////////////////////////////////////////////

    /**
     * Checkout success callback
     * @param {Checkout.ISubmitSuccessEventData} checkoutSuccessData
     */
    protected onCheckoutSuccess(checkoutSuccessData: Checkout.ISubmitSuccessEventData): void {
        if (checkoutSuccessData.response.result?.adyen) {
            checkoutSuccessData.response.preventSuccess = true;
            this.checkoutFormComponent.status.superBusy = true;
            this.validateThreeDS2(checkoutSuccessData.response.result.adyen, () => {
                this.checkoutFormComponent.redirectToSuccess();
            });
        }
    }

    /**
     * Place order, checkout submission continues
     * @param {Adyen.ICardSubmissionData} cardSubmissionData
     * @param {Checkout.ISubmissionData} checkoutSubmissionData
     * @param {() => Promise<any>} next
     */
    protected placeOrder(
        paymentSubmissionData: Adyen.IPaymentSubmissionData,
        checkoutSubmissionData: Checkout.ISubmissionData,
        next: () => Promise<any>
    ) {
        checkoutSubmissionData.paymentMethod = paymentSubmissionData;
        this.checkoutFormComponent.status.data.busy = false;
        next();
    }

    /**
     * ThreeDS2 on error callback
     * @param {any} result
     * @param {boolean} reinitCardComponent
     */
    protected onError(result?: any, reinitCardComponent = false) {
        this.checkoutFormComponent.status.superBusy = false;
        if (result && result.error) {
            this.checkoutFormComponent.status.error = result.error;
        } else {
            // tslint:disable-next-line: quotemark
            this.checkoutFormComponent.status.error = "Sorry, can't use this credit card.";
        }
        this.checkoutFormComponent.status.errorData = result;
        if (reinitCardComponent) {
            this.initComponent();
        }
        this.adyen.is3DSInProgress = false;
    }

    ////////////////////////////////////////////
    ////////
    ////////    ThreeDS2 components methods
    ////////
    ////////////////////////////////////////////

    /**
     * Validates threeDS2 adyen request:
     * - render new component or call onSuccess callback
     * @param {Adyen.IPayment} response
     * @param {ICallableFunction} onSuccess
     */
    protected validateThreeDS2(response: Adyen.IPayment, onSuccess: ICallableFunction) {
        if (response.threeDS2) {
            this.adyen.is3DSInProgress = true;
            this.renderThreeDS2Component(response, (processResponse: Adyen.IPayment) => {
                this.validateThreeDS2(processResponse, onSuccess);
            });
        } else {
            this.adyen.is3DSInProgress = false;
            onSuccess();
        }
    }

    /**
     * Renders threeDS2 component - IdentifyShopper or ChallengeShopper based on adyen payment or adyen threeDS2 payment request response
     * @param {Adyen.IPayment} response
     * @param {Adyen.IThreeDS2OnProcessSuccess} onProcessComplete
     */
    protected renderThreeDS2Component(response: Adyen.IPayment, onProcessComplete: Adyen.IThreeDS2OnProcessSuccess): void {
        const { token, type } = response;

        const onComplete: Adyen.IThreeDS2ComponentOnComplete = (result: Adyen.IThreeDS2ComponentResult) => {
            this.adyen.processThreeDS2(undefined, result && result.data, onProcessComplete, error => {
                const errorError = error && error.error;
                if (errorError === 504) {
                    onComplete();
                } else {
                    this.onError(error, true);
                }
            });
        };

        const options: Adyen.IThreeDS2ComponentOptions = {
            onComplete,
            onError: error => {
                this.adyen.cancelThreeDS(
                    undefined,
                    () => {
                        this.onError(error, true);
                    },
                    () => {
                        location.reload();
                    }
                );
            }
        };

        this.unmount();
        if (type === Adyen.ThreeDS2Type.IdentifyShopper) {
            const deviceFingerprintOptions: Adyen.IDeviceFingerprintComponentOptions = {
                ...options,
                fingerprintToken: token
            };

            this.threeDS2IdentifyComponent = this.getComponent(Adyen.Component.ThreeDS2DeviceFingerprint, deviceFingerprintOptions);
            if (this.threeDS2IdentifyComponent) {
                this.mount(this.threeDS2IdentifyComponent);
            }
        } else if (type === Adyen.ThreeDS2Type.ChallengeShopper) {
            const challengeOptions: Adyen.IChallengeComponentOptions = { ...options, challengeToken: token };
            this.threeDS2ChallengeComponent = this.getComponent(Adyen.Component.ThreeDS2Challenge, challengeOptions);
            if (this.threeDS2ChallengeComponent) {
                this.mount(this.threeDS2ChallengeComponent);
                this.scrollTo(this.getContainerElement());
            }
        }
    }
}